import React, {useRef, useEffect, useState, useCallback} from 'react';
import './Chat.css';
import ChatBubble from "../ChatBubble/ChatBubble";
import {useMount} from "./Chat.hooks";
import UserChatInput from "../UserChatInput/UserChatInput";
import ChatLoader from "../ChatLoader/ChatLoader";
import ChatYesNo from "../ChatYesNo/ChatYesNo";

const BASE_URL = process.env.REACT_APP_HR_FRENZY_API;

type IRole = 'assistant' | 'user';

interface IMessage {
    role: IRole;
    message: string;
}

const decoder = new TextDecoder('utf-8');
const startChat = 'start-chat';
const summarize = 'summarize';
const moreInfo = 'more-info';
const clarify = 'clarify';
const chat = 'chat';

const USER: IRole = 'user';
const ASSISTANT: IRole = 'assistant';
const Chat = () => {
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const messageWriteIndexRef = useRef(0);
    const [isWaitingOnResp, setIsWaitingOnResp] = useState(true);
    const [userGreeted, setUserGreeted] = useState(false);
    const [messages, setMessages] = useState<IMessage[]>([]);
    const [isProblemConfirmed, setIsProblemConfirmed] = useState(false);
    const [problem, setProblem] = useState('');
    const [showYesNo, setShowYesNo] = useState(false);

    const incrementIndex = () => {
        messageWriteIndexRef.current += 1;
    };
    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    const updateMessages = useCallback((role: IMessage["role"], updatedObject: Record<string, string>, writeIndex: number) => {
        setMessages(prevMessages => {
            const updatedList: IMessage[] = [...prevMessages]
            const existingMessage = updatedList[writeIndex]?.message || '';
            updatedList[writeIndex] = {
                role,
                message: existingMessage + updatedObject.response || ''
            };
            return updatedList;
        });
    }, []);

    const fetchSummary = useCallback((userInput: string) => {
        fetch(`${BASE_URL}/${summarize}`, {
            method: 'POST',
            headers: {
                'Accepts': '*/*',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                prompt: userInput,
            })
        }).then(response => {
            const reader = response.body?.getReader();
            reader?.read().then(function processText({done, value}) {
                if (done) {
                    incrementIndex();
                    setShowYesNo(true);
                    return;
                }

                setIsWaitingOnResp(false);

                updateMessages(ASSISTANT, {
                    response: decoder.decode(value),
                }, messageWriteIndexRef.current);

                reader.read().then(processText)
            })
        }).catch(error => {
            console.error('<><><><>  chat error', error);
        })
    }, [updateMessages]);

    const doChat = useCallback((userInput: string) => {
        //TODO: serialize some of the chat
        fetch(`${BASE_URL}/${chat}`, {
            method: 'POST',
            headers: {
                'Accepts': '*/*',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                messages,
                prompt: userInput,
                problem: problem,
            })
        }).then(response => {
            const reader = response.body?.getReader();
            reader?.read().then(function processText({done, value}) {
                if (done) {
                    incrementIndex()
                    return;
                }

                setIsWaitingOnResp(false);

                updateMessages(ASSISTANT, {
                    response: decoder.decode(value),
                }, messageWriteIndexRef.current);

                reader.read().then(processText)
            })
        }).catch(error => {
            console.error('<><><><>  chat error', error);
        })
    }, [messages, updateMessages])

    const randomDelay = () => {
        const delay = 750 + Math.random() * 500;

        return new Promise(resolve => {
            setTimeout(resolve, delay);
        })
    }

    const handleUserInput = useCallback((userInput: string, userConfirmedIssue: boolean) => {
        const message: IMessage = {
            role: USER,
            message: userInput,
        };

        updateMessages(message.role, {
            response: message.message,
        }, messageWriteIndexRef.current);

        incrementIndex()
        setIsWaitingOnResp(true);
        randomDelay().then(() => {
            if (!userConfirmedIssue) {
                fetchSummary(userInput);
            } else {
                doChat(userInput);
            }
        });
    }, [doChat, fetchSummary, updateMessages])

    const fetchUserGreeting = useCallback(() => {
        fetch(`${BASE_URL}/${startChat}`, {
            headers: {
                'Accepts': '*/*',
                'Content-Type': 'application/json',
            },
        }).then(response => {
            const reader = response.body?.getReader();
            reader?.read().then(function processText({done, value}) {
                if (done) {
                    incrementIndex();
                    return;
                }

                setIsWaitingOnResp(false);

                updateMessages(ASSISTANT, {
                    response: decoder.decode(value),
                }, messageWriteIndexRef.current);

                reader.read().then(processText)
            })
        }).catch(error => {
            console.error('<><><><>  error', error);
        })
    }, [updateMessages]);

    const fetchCompanyInfo = () => {
        setIsWaitingOnResp(true);
        fetch(`${BASE_URL}/${moreInfo}`, {
            headers: {
                'Accepts': '*/*',
                'Content-Type': 'application/json',
            },
        }).then(response => {
            const reader = response.body?.getReader();
            reader?.read().then(function processText({done, value}) {
                if (done) {
                    incrementIndex();
                    return;
                }

                setIsWaitingOnResp(false);

                updateMessages(ASSISTANT, {
                    response: decoder.decode(value),
                }, messageWriteIndexRef.current);

                reader.read().then(processText)
            })
        }).catch(error => {
            console.error('<><><><>  error', error);
        })
    };

    const fetchClarification = () => {
        setIsWaitingOnResp(true);
        fetch(`${BASE_URL}/${clarify}`, {
            headers: {
                'Accepts': '*/*',
                'Content-Type': 'application/json',
            },
        }).then(response => {
            const reader = response.body?.getReader();
            reader?.read().then(function processText({done, value}) {
                if (done) {
                    incrementIndex();
                    return;
                }

                setIsWaitingOnResp(false);

                updateMessages(ASSISTANT, {
                    response: decoder.decode(value),
                }, messageWriteIndexRef.current);

                reader.read().then(processText)
            })
        }).catch(error => {
            console.error('<><><><>  error', error);
        })
    };

    const clickYesHandler = () => {
        setShowYesNo(false);
        updateMessages(USER, {response: 'Yes'}, messageWriteIndexRef.current)
        setIsProblemConfirmed(true);
        setProblem(messages.at(messageWriteIndexRef.current - 2)?.message ?? '')
        incrementIndex();
        randomDelay().then(() => fetchCompanyInfo());
    };

    const clickNoHandler = () => {
        setShowYesNo(false);
        updateMessages(USER, {response: 'No'}, messageWriteIndexRef.current)
        setIsProblemConfirmed(false);
        incrementIndex();
        randomDelay().then(() => fetchClarification());
    }

    useEffect(() => {
        if (!userGreeted) {
            setUserGreeted(true)
            fetchUserGreeting();
        }
        scrollToBottom();
    }, [messages]);

    // useMount(() => {
    //     if (!userGreeted) {
    //         setUserGreeted(true)
    //         fetchUserGreeting();
    //     }
    // });

    return (
        <>
            <div
                className="chat-container"
                ref={chatContainerRef}
            >
                {messages.map((message, index) => (
                    <ChatBubble
                        key={index}
                        role={message.role}
                        message={message.message}/>
                ))}
                {isWaitingOnResp && <ChatLoader/>}
            </div>
            {showYesNo
                ? <ChatYesNo clickYes={clickYesHandler} clickNo={clickNoHandler}/>
                : <UserChatInput onSendMessage={str => handleUserInput(str, isProblemConfirmed)}/>
            }
        </>
    );
};

export default Chat;